import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, User, Lock, Bell, Shield, Plug, Database, Upload, Trash2, AlertTriangle, RefreshCw, Download, Edit, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import IntegrationsContent from '@/components/IntegrationsContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SettingsPage() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile state
  const [fullName, setFullName] = useState('');
  const [contactNo, setContactNo] = useState('');
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [leadAssignmentNotif, setLeadAssignmentNotif] = useState(true);
  const [dailySummary, setDailySummary] = useState(false);
  
  // Master Data Management state
  const [projectsFile, setProjectsFile] = useState(null);
  const [salesRepsFile, setSalesRepsFile] = useState(null);
  const [sourcesFile, setSourcesFile] = useState(null);
  
  // Change 3: Export with filters state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    project: '',
    status: '',
    source: '',
    salesRep: '',
    fromDate: '',
    toDate: ''
  });
  const [availableProjects, setAvailableProjects] = useState([]);
  const [availableSources, setAvailableSources] = useState([]);
  const [availableSalesReps, setAvailableSalesReps] = useState([]);
  
  // Source Management state
  const [sourcesData, setSourcesData] = useState([]);
  const [showAddSourceDialog, setShowAddSourceDialog] = useState(false);
  const [showEditSourceDialog, setShowEditSourceDialog] = useState(false);
  const [showAddSubSourceDialog, setShowAddSubSourceDialog] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');
  const [editingSource, setEditingSource] = useState(null);
  const [editSourceName, setEditSourceName] = useState('');
  const [selectedSourceForSubSource, setSelectedSourceForSubSource] = useState('');
  const [newSubSourceName, setNewSubSourceName] = useState('');
  
  // Organization settings state
  const [orgName, setOrgName] = useState('Real Estate CRM');
  const [orgLogo, setOrgLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [profileSubTab, setProfileSubTab] = useState('general'); // New state for profile sub-tabs
  
  const isSuperAdmin = user?.user_role === 'Super Administrator';

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || '');
      setContactNo(user.contact_no || '');
    }
  }, [user]);

  const handleUpdateProfile = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      await axios.put(
        `${API}/users/profile`,
        { full_name: fullName, contact_no: contactNo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profile updated successfully');
      window.location.reload(); // Refresh to update user context
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/users/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifications = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      await axios.put(
        `${API}/users/preferences`,
        {
          email_notifications: emailNotifications,
          sms_notifications: smsNotifications,
          lead_assignment_notif: leadAssignmentNotif,
          daily_summary: dailySummary
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Preferences saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  // Master Data Management handlers
  const handleUploadProjects = async () => {
    if (!projectsFile) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', projectsFile);

    try {
      const response = await axios.post(`${API}/master/upload-projects`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      setProjectsFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload projects');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSalesReps = async () => {
    if (!salesRepsFile) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', salesRepsFile);

    try {
      const response = await axios.post(`${API}/master/upload-brokers`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      setSalesRepsFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload sales reps');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncProjectAssignments = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const response = await axios.post(`${API}/master/sync-project-assignments`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync project assignments');
    } finally {
      setLoading(false);
    }

  };

  // Change 3: Load filter options for export dialog
  const loadExportFilterOptions = async () => {
    const token = localStorage.getItem('token');
    try {
      const [projectsRes, sourcesRes, salesRepsRes] = await Promise.all([
        axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/master/sources`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/brokers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAvailableProjects(projectsRes.data.map(p => p.project_name));
      setAvailableSources(sourcesRes.data.map(s => s.value)); // Fix: sources use 'value' not 'source_name'
      setAvailableSalesReps(salesRepsRes.data.filter(b => b.user_role === 'Sales Rep'));
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  };

  const openExportDialog = async () => {
    await loadExportFilterOptions();
    setExportFilters({
      project: '',
      status: '',
      source: '',
      salesRep: '',
      fromDate: '',
      toDate: ''
    });
    setShowExportDialog(true);
  };

  // Change 3: Check if any export filter is selected
  const hasExportFilters = exportFilters.project || exportFilters.status || 
    exportFilters.source || exportFilters.salesRep || 
    exportFilters.fromDate || exportFilters.toDate;

  const handleExportAllLeads = async () => {
    if (!hasExportFilters) {
      toast.error('Please select at least one filter before exporting');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      // Build query params from filters
      const params = new URLSearchParams();
      if (exportFilters.project) params.append('project', exportFilters.project);
      if (exportFilters.status) params.append('status', exportFilters.status);
      if (exportFilters.source) params.append('source', exportFilters.source);
      if (exportFilters.salesRep) params.append('assigned_to', exportFilters.salesRep);
      if (exportFilters.fromDate) params.append('from_date', exportFilters.fromDate);
      if (exportFilters.toDate) params.append('to_date', exportFilters.toDate);

      const response = await axios.get(`${API}/leads/export/all?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `leads_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Leads exported successfully');
      setShowExportDialog(false);
    } catch (error) {
      // Handle blob error response
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          toast.error(json.detail || 'Failed to export leads');
        } catch {
          toast.error('Failed to export leads');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to export leads');
      }
    } finally {
      setLoading(false);
    }
  };

  // Organization settings functions
  const loadOrganizationSettings = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/organization/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setOrgName(response.data.organization_name || 'Real Estate CRM');
        setLogoPreview(response.data.logo_base64 || '');
      }
    } catch (error) {
      // If no organization settings exist, keep defaults
      console.log('No organization settings found, using defaults');
    }
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('Logo file must be less than 2MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      
      setOrgLogo(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateOrganization = async () => {
    if (!orgName.trim()) {
      toast.error('Organization name is required');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const formData = new FormData();
      formData.append('organization_name', orgName);
      if (orgLogo) {
        formData.append('logo', orgLogo);
      }

      await axios.post(
        `${API}/organization/settings`,
        formData,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Organization settings updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update organization settings');
    } finally {
      setLoading(false);
    }
  };

  // Source Management functions
  const loadSourcesData = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/master/source-management`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSourcesData(response.data);
    } catch (error) {
      toast.error('Failed to load sources data');
    }
  };

  const handleAddSource = async () => {
    if (!newSourceName.trim()) {
      toast.error('Source name is required');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/master/source`,
        { source_name: newSourceName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Source added successfully');
      setNewSourceName('');
      setShowAddSourceDialog(false);
      loadSourcesData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add source');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubSource = async () => {
    if (!selectedSourceForSubSource || !newSubSourceName.trim()) {
      toast.error('Please select a source and enter sub-source name');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/master/sub-source`,
        { 
          source_name: selectedSourceForSubSource,
          sub_source_name: newSubSourceName 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sub-source added successfully');
      setNewSubSourceName('');
      setSelectedSourceForSubSource('');
      setShowAddSubSourceDialog(false);
      loadSourcesData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add sub-source');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSource = async () => {
    if (!editSourceName.trim()) {
      toast.error('Please enter a source name');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      await axios.put(`${API}/master/source/${editingSource.id}`, {
        source_name: editSourceName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Source updated successfully');
      setEditSourceName('');
      setEditingSource(null);
      setShowEditSourceDialog(false);
      loadSourcesData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update source');
    } finally {
      setLoading(false);
    }
  };

  const openEditSourceDialog = (sourceItem) => {
    setEditingSource(sourceItem.source);
    setEditSourceName(sourceItem.source.label);
    setShowEditSourceDialog(true);
  };

  const handleDeleteSubSource = async (subSourceId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(
        `${API}/master/sub-source/${subSourceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sub-source deleted successfully');
      loadSourcesData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sub-source');
    }
  };

  const handleUploadSources = async () => {
    if (!sourcesFile) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', sourcesFile);

    try {
      const response = await axios.post(`${API}/master/sources/bulk-upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      setSourcesFile(null);
      loadSourcesData();
      
      // Show errors if any
      if (response.data.errors && response.data.errors.length > 0) {
        toast.error(`Some errors occurred: ${response.data.errors.slice(0, 3).join(', ')}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload sources');
    } finally {
      setLoading(false);
    }
  };

  // Load sources data when in master data tab
  useEffect(() => {
    if (activeTab === 'master-data' && isSuperAdmin) {
      loadSourcesData();
    }
  }, [activeTab, isSuperAdmin]);


  // Load organization settings on component mount
  useEffect(() => {
    if (isSuperAdmin) {
      loadOrganizationSettings();
    }
  }, [isSuperAdmin]);

  const isAdmin = user?.user_role === 'Super Administrator' || user?.user_role === 'Manager';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="settings-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Settings
              </h1>
              <p className="text-sm text-slate-600">Manage your account and preferences</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`grid w-full ${isSuperAdmin ? 'grid-cols-4' : isAdmin ? 'grid-cols-2' : 'grid-cols-1'} mb-6`}>
            <TabsTrigger value="profile" data-testid="profile-tab">
              <User className="mr-2 h-4 w-4" />
              Profile
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="integrations" data-testid="integrations-tab">
                <Plug className="mr-2 h-4 w-4" />
                Integrations
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="organization" data-testid="organization-tab">
                <Shield className="mr-2 h-4 w-4" />
                Organization
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="master-data" data-testid="master-data-tab">
                <Database className="mr-2 h-4 w-4" />
                Master Data
              </TabsTrigger>
            )}
          </TabsList>

          {/* Profile Tab with Sub-tabs */}
          <TabsContent value="profile">
            <Card data-testid="profile-card">
              <CardContent className="p-0">
                <Tabs value={profileSubTab} onValueChange={setProfileSubTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-3 rounded-b-none">
                    <TabsTrigger value="general">
                      <User className="mr-2 h-4 w-4" />
                      General
                    </TabsTrigger>
                    <TabsTrigger value="security">
                      <Lock className="mr-2 h-4 w-4" />
                      Security
                    </TabsTrigger>
                    <TabsTrigger value="notifications">
                      <Bell className="mr-2 h-4 w-4" />
                      Notifications
                    </TabsTrigger>
                  </TabsList>

                  {/* General Sub-tab */}
                  <TabsContent value="general" className="m-0">
                    <Card className="border-0 shadow-none">
                      <CardHeader>
                        <CardTitle>Profile Information</CardTitle>
                        <CardDescription>Update your personal details</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                              id="email"
                              type="email"
                              value={user?.email || ''}
                              disabled
                              className="bg-slate-50"
                            />
                            <p className="text-xs text-slate-500">Email cannot be changed</p>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="full_name">Full Name</Label>
                            <Input
                              id="full_name"
                              value={fullName}
                              onChange={(e) => setFullName(e.target.value)}
                              data-testid="full-name-input"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="contact_no">Contact Number</Label>
                            <Input
                              id="contact_no"
                              value={contactNo}
                              onChange={(e) => {
                                // Allow + prefix and digits, with optional formatting (spaces, dashes, parentheses)
                                let value = e.target.value;
                                // Remove all non-digit, non-formatting characters except + at start
                                value = value.replace(/[^\d+\s\-()]/g, '');
                                // Ensure + only appears at the start
                                if (value.includes('+') && !value.startsWith('+')) {
                                  value = '+' + value.replace(/\+/g, '');
                                }
                                // Limit to 20 characters to allow for formatting
                                if (value.length <= 20) {
                                  setContactNo(value);
                                }
                              }}
                              placeholder="Enter mobile number (e.g., +1234567890 or 1234567890)"
                              data-testid="contact-no-input"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Role</Label>
                            <div className="flex items-center gap-2">
                              <Shield className="h-4 w-4 text-cyan-600" />
                              <span className="font-medium text-slate-700">{user?.user_role}</span>
                            </div>
                          </div>

                          {user?.projects_assigned && (
                            <div className="space-y-2">
                              <Label>Projects Assigned</Label>
                              <div className="bg-slate-50 p-3 rounded-lg">
                                <span className="text-sm font-medium text-slate-700">
                                  {user.projects_assigned.length} project(s)
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        <Separator />

                        <Button
                          onClick={handleUpdateProfile}
                          disabled={loading}
                          className="bg-cyan-600 hover:bg-cyan-700"
                          data-testid="save-profile-button"
                        >
                          {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Security Sub-tab */}
                  <TabsContent value="security" className="m-0">
                    <Card className="border-0 shadow-none" data-testid="security-card">
                      <CardHeader>
                        <CardTitle>Change Password</CardTitle>
                        <CardDescription>Update your password to keep your account secure</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="current_password">Current Password</Label>
                            <Input
                              id="current_password"
                              type="password"
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              placeholder="Enter current password"
                              data-testid="current-password-input"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="new_password">New Password</Label>
                            <Input
                              id="new_password"
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Enter new password"
                              data-testid="new-password-input"
                            />
                            <p className="text-xs text-slate-500">Minimum 8 characters</p>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="confirm_password">Confirm New Password</Label>
                            <Input
                              id="confirm_password"
                              type="password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              placeholder="Confirm new password"
                              data-testid="confirm-password-input"
                            />
                          </div>
                        </div>

                        <Separator />

                        <div className="flex gap-3">
                          <Button
                            onClick={handleChangePassword}
                            disabled={loading}
                            className="bg-cyan-600 hover:bg-cyan-700"
                            data-testid="change-password-button"
                          >
                            {loading ? 'Changing...' : 'Change Password'}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setCurrentPassword('');
                              setNewPassword('');
                              setConfirmPassword('');
                            }}
                          >
                            Clear
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Notifications Sub-tab */}
                  <TabsContent value="notifications" className="m-0">
                    <Card className="border-0 shadow-none" data-testid="notifications-card">
                      <CardHeader>
                        <CardTitle>Notification Preferences</CardTitle>
                        <CardDescription>Choose how you want to be notified</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label>Email Notifications</Label>
                              <p className="text-sm text-slate-500">Receive updates via email</p>
                            </div>
                            <Switch
                              checked={emailNotifications}
                              onCheckedChange={setEmailNotifications}
                              data-testid="email-notifications-toggle"
                            />
                          </div>

                          <Separator />

                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label>SMS Notifications</Label>
                              <p className="text-sm text-slate-500">Receive updates via SMS</p>
                            </div>
                            <Switch
                              checked={smsNotifications}
                              onCheckedChange={setSmsNotifications}
                              data-testid="sms-notifications-toggle"
                            />
                          </div>

                          <Separator />

                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label>Lead Assignment Alerts</Label>
                              <p className="text-sm text-slate-500">Get notified when leads are assigned to you</p>
                            </div>
                            <Switch
                              checked={leadAssignmentNotif}
                              onCheckedChange={setLeadAssignmentNotif}
                              data-testid="lead-assignment-toggle"
                            />
                          </div>

                          <Separator />

                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label>Daily Summary Report</Label>
                              <p className="text-sm text-slate-500">Receive daily summary of your activities</p>
                            </div>
                            <Switch
                              checked={dailySummary}
                              onCheckedChange={setDailySummary}
                              data-testid="daily-summary-toggle"
                            />
                          </div>
                        </div>

                        <Separator />

                        <Button
                          onClick={handleSaveNotifications}
                          disabled={loading}
                          className="bg-cyan-600 hover:bg-cyan-700"
                          data-testid="save-notifications-button"
                        >
                          {loading ? 'Saving...' : 'Save Preferences'}
                        </Button>

                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                          <p className="text-xs text-blue-800">
                            <strong>Note:</strong> Email and SMS notifications require additional configuration. 
                            Contact your administrator for setup.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integrations Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="integrations">
              <IntegrationsContent />
            </TabsContent>
          )}

          {/* Organization Tab - Super Admin Only */}
          {isSuperAdmin && (
            <TabsContent value="organization">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Shield className="mr-2 h-5 w-5" />
                      Organization Settings
                    </CardTitle>
                    <CardDescription>
                      Customize your organization's branding and information displayed on the login page.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Organization Name */}
                    <div className="space-y-2">
                      <Label htmlFor="org-name">Organization Name</Label>
                      <Input
                        id="org-name"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        placeholder="Enter organization name"
                        data-testid="org-name-input"
                      />
                      <p className="text-sm text-slate-500">
                        This name will be displayed on the login page instead of "Real Estate CRM"
                      </p>
                    </div>

                    {/* Organization Logo */}
                    <div className="space-y-2">
                      <Label htmlFor="org-logo">Organization Logo</Label>
                      <div className="space-y-4">
                        {logoPreview && (
                          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                            <img 
                              src={logoPreview} 
                              alt="Logo preview" 
                              className="w-16 h-16 object-contain rounded-lg border"
                            />
                            <div>
                              <p className="text-sm font-medium text-slate-800">Current Logo</p>
                              <p className="text-xs text-slate-500">Will be displayed on login page</p>
                            </div>
                          </div>
                        )}
                        
                        <Input
                          id="org-logo"
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          data-testid="org-logo-input"
                        />
                        <p className="text-sm text-slate-500">
                          Upload a logo image (PNG, JPG, etc.). Maximum file size: 2MB. Logo will be displayed on the login page.
                        </p>
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="space-y-2">
                      <Label>Login Page Preview</Label>
                      <div className="p-6 bg-slate-100 rounded-lg border-2 border-dashed border-slate-300">
                        <div className="text-center space-y-2">
                          {logoPreview && (
                            <img 
                              src={logoPreview} 
                              alt="Logo preview" 
                              className="w-12 h-12 object-contain mx-auto"
                            />
                          )}
                          <h3 className="text-xl font-bold text-slate-800">{orgName || 'Organization Name'}</h3>
                          <p className="text-sm text-slate-600">Enter your credentials to access your account</p>
                        </div>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="pt-4">
                      <Button 
                        onClick={handleUpdateOrganization}
                        disabled={loading}
                        className="bg-cyan-600 hover:bg-cyan-700"
                        data-testid="save-organization-button"
                      >
                        {loading ? 'Saving...' : 'Save Organization Settings'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Master Data Tab - Super Admin Only */}
          {isSuperAdmin && (
            <TabsContent value="master-data">
              <div className="space-y-6">
                {/* Upload Projects */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Upload className="mr-2 h-5 w-5" />
                      Upload Projects
                    </CardTitle>
                    <CardDescription>
                      Upload projects from Excel/CSV file. Required column: "Project Name"
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="projects-file">Select File</Label>
                      <Input
                        id="projects-file"
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => setProjectsFile(e.target.files[0])}
                      />
                      {projectsFile && (
                        <p className="text-sm text-slate-600">Selected: {projectsFile.name}</p>
                      )}
                    </div>
                    <Button
                      onClick={handleUploadProjects}
                      disabled={!projectsFile || loading}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Upload Projects
                    </Button>
                  </CardContent>
                </Card>

                {/* Upload Sales Reps */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Upload className="mr-2 h-5 w-5" />
                      Upload Sales Reps/Users
                    </CardTitle>
                    <CardDescription>
                      Upload sales reps from Excel/CSV. Required: "Full Name", "Email", "User Role". Optional: "Contact No.", "Projects Assigned"
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="sales-reps-file">Select File</Label>
                      <Input
                        id="sales-reps-file"
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => setSalesRepsFile(e.target.files[0])}
                      />
                      {salesRepsFile && (
                        <p className="text-sm text-slate-600">Selected: {salesRepsFile.name}</p>
                      )}
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm text-amber-800">
                        <AlertTriangle className="inline h-4 w-4 mr-1" />
                        Default password for all new users: <strong>password123</strong>
                      </p>
                    </div>
                    <Button
                      onClick={handleUploadSalesReps}
                      disabled={!salesRepsFile || loading}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Upload Sales Reps
                    </Button>
                  </CardContent>
                </Card>

                {/* Change 2: Removed Clear Data Options and Clear ALL Test Data sections */}
                
                {/* Export Leads with Filters - Change 3: Now requires filters */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-green-600">
                      <Download className="mr-2 h-5 w-5" />
                      Export Leads
                    </CardTitle>
                    <CardDescription>
                      Download leads with filters in Excel format. At least one filter must be selected.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm text-amber-800">
                        <AlertTriangle className="inline h-4 w-4 mr-1" />
                        For large datasets, please apply filters (project, status, date range, etc.) before exporting.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={openExportDialog}
                      disabled={loading}
                      className="border-green-500 text-green-600 hover:bg-green-50"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Export Leads
                    </Button>
                  </CardContent>
                </Card>

                {/* Sync Project Assignments */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-blue-600">
                      <RefreshCw className="mr-2 h-5 w-5" />
                      Sync Project Assignments
                    </CardTitle>
                    <CardDescription>
                      Clean up and sync all user project assignments. This removes any invalid project names from users' assigned projects that no longer exist in the system.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        Use this after uploading new projects or if you notice incorrect project counts in Sales Rep Management.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleSyncProjectAssignments}
                      disabled={loading}
                      className="border-blue-500 text-blue-600 hover:bg-blue-50"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Sync Project Assignments
                    </Button>
                  </CardContent>
                </Card>

                {/* Source Management */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Database className="mr-2 h-5 w-5" />
                      Source Management
                    </CardTitle>
                    <CardDescription>
                      Manage lead sources and sub-sources (Channel Partners)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setShowAddSourceDialog(true)}
                        disabled={loading}
                      >
                        Add Source
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowAddSubSourceDialog(true)}
                        disabled={loading}
                      >
                        Add Channel Partner
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = '.xlsx,.xls';
                          input.onchange = (e) => setSourcesFile(e.target.files[0]);
                          input.click();
                        }}
                        disabled={loading}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Upload Excel
                      </Button>
                    </div>
                    
                    {sourcesFile && (
                      <div className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <p className="text-sm text-slate-600 flex-1">Selected: {sourcesFile.name}</p>
                        <Button
                          size="sm"
                          onClick={handleUploadSources}
                          disabled={loading}
                        >
                          Upload
                        </Button>
                      </div>
                    )}
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        Excel file should contain columns: <strong>source</strong>, <strong>sub_source</strong>
                      </p>
                      <p className="text-sm text-blue-700 mt-1">
                        Example: source="Channel Partner", sub_source="ABC Realty Partners"
                      </p>
                    </div>

                    {/* Sources Display */}
                    {sourcesData.length > 0 && (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {sourcesData.map((sourceItem, idx) => (
                          <div key={idx} className="border rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div className="font-medium text-slate-800">{sourceItem.source.label}</div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditSourceDialog(sourceItem)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            </div>
                            {sourceItem.sub_sources.length > 0 && (
                              <div className="mt-2 pl-4 space-y-1">
                                <div className="text-xs font-medium text-slate-500 uppercase">Channel Partners:</div>
                                {sourceItem.sub_sources.map((subSource, subIdx) => (
                                  <div key={subIdx} className="flex items-center justify-between text-sm bg-slate-50 px-2 py-1 rounded">
                                    <span>{subSource.label}</span>
                                    {sourceItem.source.value === 'Channel Partner' && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDeleteSubSource(subSource.value)}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Audit Log Section - Super Admin Only */}
                {user?.user_role === 'Super Administrator' && (
                  <Card className="border-rose-200">
                    <CardHeader>
                      <CardTitle className="flex items-center text-rose-600">
                        <ClipboardList className="mr-2 h-5 w-5" />
                        Audit Log
                      </CardTitle>
                      <CardDescription>
                        View complete audit trail of all system activities
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        onClick={() => navigate('/audit-logs')}
                        className="bg-rose-600 hover:bg-rose-700"
                      >
                        <ClipboardList className="mr-2 h-4 w-4" />
                        View Audit Log
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Change 3: Export Leads with Filters Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center text-green-600">
              <Download className="mr-2 h-5 w-5" />
              Export Leads
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              Select at least one filter to export leads. This helps manage large datasets efficiently.
            </p>
            
            {/* Project Filter */}
            <div className="space-y-2">
              <Label>Project</Label>
              <select
                value={exportFilters.project}
                onChange={(e) => setExportFilters({...exportFilters, project: e.target.value})}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All Projects</option>
                {availableProjects.map(project => (
                  <option key={project} value={project}>{project}</option>
                ))}
              </select>
            </div>
            
            {/* Status Filter */}
            <div className="space-y-2">
              <Label>Status</Label>
              <select
                value={exportFilters.status}
                onChange={(e) => setExportFilters({...exportFilters, status: e.target.value})}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All Statuses</option>
                {['New', 'Attempted - 1', 'Attempted - 2', 'Attempted - 3',
                  'Site Visit Planned', 'Site Visit Done', 'Follow up',
                  'Booked', 'Lost', 'Unqualified', 'Dropped after site visit'].map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            
            {/* Source Filter */}
            <div className="space-y-2">
              <Label>Source</Label>
              <select
                value={exportFilters.source}
                onChange={(e) => setExportFilters({...exportFilters, source: e.target.value})}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All Sources</option>
                {availableSources.map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
            </div>
            
            {/* Sales Rep Filter */}
            <div className="space-y-2">
              <Label>Sales Rep</Label>
              <select
                value={exportFilters.salesRep}
                onChange={(e) => setExportFilters({...exportFilters, salesRep: e.target.value})}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All Sales Reps</option>
                {availableSalesReps.map(rep => (
                  <option key={rep.id} value={rep.id}>{rep.full_name}</option>
                ))}
              </select>
            </div>
            
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={exportFilters.fromDate}
                  onChange={(e) => setExportFilters({...exportFilters, fromDate: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={exportFilters.toDate}
                  onChange={(e) => setExportFilters({...exportFilters, toDate: e.target.value})}
                />
              </div>
            </div>
            
            {!hasExportFilters && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">
                  <AlertTriangle className="inline h-4 w-4 mr-1" />
                  Please select at least one filter before exporting.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExportAllLeads} 
              disabled={loading || !hasExportFilters}
              className="bg-green-600 hover:bg-green-700"
            >
              <Download className="mr-2 h-4 w-4" />
              {loading ? 'Exporting...' : 'Export Leads'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Source Dialog */}
      <Dialog open={showAddSourceDialog} onOpenChange={setShowAddSourceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Source</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Source Name</Label>
              <Input
                value={newSourceName}
                onChange={(e) => setNewSourceName(e.target.value)}
                placeholder="e.g., Walk-in, Referral, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSourceDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSource} disabled={loading}>
              Add Source
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Sub-Source Dialog */}
      <Dialog open={showAddSubSourceDialog} onOpenChange={setShowAddSubSourceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Channel Partner</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Select Source</Label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
                value={selectedSourceForSubSource}
                onChange={(e) => setSelectedSourceForSubSource(e.target.value)}
              >
                <option value="">Select a source...</option>
                {sourcesData.map((sourceItem, idx) => (
                  <option key={idx} value={sourceItem.source.label}>
                    {sourceItem.source.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Channel Partner Name</Label>
              <Input
                value={newSubSourceName}
                onChange={(e) => setNewSubSourceName(e.target.value)}
                placeholder="e.g., ABC Realty Partners"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSubSourceDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSubSource} disabled={loading}>
              Add Channel Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SettingsPage;
